import { collection, list, named, num, toggle } from '../../themes/settingFields';
import { element } from './definition';
import { SLIDER_ARROWS } from './layouts';
import { look, paint, radius, round, typeface } from './styleTokens';

// The two rows a shop's home page is actually built out of: the collections a merchant has grouped
// their products into, and the products inside one of them. Neither carries any content of its own —
// every word and every picture in them is the merchant's own catalog — so what a merchant edits here
// is how many, how wide, and what the card says around the parts the shop fills in.
//
// This is the difference between a theme that demos and a theme that ships. A hand-typed row of three
// cards looks right in a screenshot and is wrong the moment the merchant adds a fourth collection.

/** How many across, at the widest — one for a row in a narrow column. Narrower screens fold this down in the element's own stylesheet. */
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 4;

/** How many records a row may draw at most, matching the bound the read itself enforces. */
const MIN_CARDS = 1;
const MAX_CARDS = 24;

/** The collections row: one card per collection, each a link into it. */
export const collectionListElement = element('collection-list', {
  label: 'Collection cards',
  description: 'A row of cards, one for each of your collections, with its picture and how many it holds.',
  category: 'Other',
  icon: 'boxes',
  props: [
    { ...list('collections', 'Selected collections', [collection('collection', 'Collection', 'Choose a collection')]), help: 'Arrange these in the order you want. Leave empty to show your newest collections.' },
    num('limit', 'How many to show', MIN_CARDS, MAX_CARDS),
    num('columns', 'Across a desktop', MIN_COLUMNS, MAX_COLUMNS),
    toggle('sampleContent', 'Show theme samples when empty'),
    { ...toggle('showCount', 'Show how many are in each'), help: 'Counted from your catalogue, never typed.' },
    { ...toggle('swipeOnPhone', 'Swipe sideways on a phone'), help: 'One row a shopper swipes through, instead of one card under another.' },
    { ...named('layout', 'Layout', [['grid', 'Grid'], ['carousel', 'Slider'], ['mosaic', 'Mosaic']]), help: 'Mosaic: a narrow card beside a wide one, then three alike.' }
  ],
  defaults: {
    props: { limit: 3, columns: 3, showCount: true },
    styles: look({
      display: 'grid', gap: '24px', width: '100%',
      fontFamily: typeface('body'), color: paint('heading')
    })
  }
});

/** The reviews row: the store's own approved reviews, each drawn with the card inside it. */
export const reviewListElement = element('review-list', {
  label: 'Reviews',
  description: 'Your approved reviews, each drawn with the card inside it. On a product page shoppers can write one; elsewhere it shows nothing until you have some.',
  category: 'Other',
  icon: 'star',
  props: [
    num('limit', 'How many to show', 1, 12),
    num('columns', 'Across a desktop', MIN_COLUMNS, MAX_COLUMNS),
    named('layout', 'Layout', [['grid', 'Grid'], ['carousel', 'Slider']]),
    { ...named('arrows', 'Slider arrows', SLIDER_ARROWS), help: 'Where a slider\'s two arrows sit. A phone swipes.' },
    { ...toggle('ofProduct', 'Only this page\'s product'), help: 'On a product page: the reviews of the product shown. Off: your newest reviews of any product.' },
    { ...toggle('hideWriteReview', 'Hide “Write a review”'), help: 'With this page\'s product only: shoppers write a review under the list, and you approve it before it shows.' },
    toggle('sampleContent', 'Show theme samples while you have no reviews')
  ],
  defaults: { props: { limit: 6, columns: 3, layout: 'carousel', sampleContent: true }, styles: look({ width: '100%' }) }
});

/** The products row: what is inside one collection, drawn as the merchant merchandised it. */
export const productListElement = element('product-list', {
  label: 'Product cards',
  description: 'A grid of products from one of your collections, with their pictures and prices.',
  category: 'Other',
  icon: 'shopping-bag',
  props: [
    { ...named('source', 'Products from', [['collection', 'A collection, or all products newest first'], ['best-selling', 'Best sellers'], ['on-sale', 'Products on sale'], ['under-price', 'Under a price'], ['search', 'What the shopper searched for'], ['listing', 'This page’s products, with its filters and pages']]), help: 'Best sellers are what sold most in the last 90 days. “This page’s products” is the collection or search the address names, narrowed by the Filters and paged by the Pages on the same page.' },
    { ...num('maxPrice', 'Price ceiling', 0, 100000), help: 'For “Under a price”: products with an option at or under this price.' },
    { ...collection('collection', 'Collection', 'All products'), help: 'Which of your collections these come from.' },
    { ...num('limit', 'How many to show', MIN_CARDS, MAX_CARDS), help: 'In the order you merchandised them.' },
    num('columns', 'Across a desktop', MIN_COLUMNS, MAX_COLUMNS),
    named('layout', 'Layout', [['grid', 'Grid'], ['carousel', 'Slider']]),
    { ...named('arrows', 'Slider arrows', SLIDER_ARROWS), help: 'Where a slider\'s two arrows sit. A phone swipes.' },
    { ...toggle('stagger', 'Set every second column lower'), help: 'A zigzag instead of straight rows, on a desktop.' },
    { ...toggle('swipeOnPhone', 'Swipe sideways on a phone'), help: 'One row a shopper swipes through, instead of two cards a line.' },
    toggle('showPrice', 'Show the price'),
    toggle('sampleContent', 'Show theme samples when empty')
  ],
  defaults: {
    props: { collection: '', limit: 4, columns: 4, showPrice: true },
    styles: look({
      display: 'grid', gap: '20px', width: '100%',
      fontFamily: typeface('body'), color: paint('heading'), ...radius(round('md'))
    })
  }
});
